import useDatadog from '~/hooks/useDatadog'
import useAmplitude from '~/hooks/useAmplitude'
import Cookies from 'js-cookie'
export const pageViewTrackingEnrichment = () => {
  return {
    name: 'page-view-tracking-enrichment',
    type: 'enrichment',
    execute: async event => {
      try {
        const currentSessionId = event.session_id

        if (currentSessionId) {
          const AMPSessionId = Cookies.get('AMP_session_id')
          if (AMPSessionId !== currentSessionId.toString()) {
            Cookies.set('AMP_session_id', currentSessionId)
            event.user_properties = {
              ...event.user_properties,
              EntryPage: new URL(window.location.href).pathname,
              ScreenResolution: {
                width: window.screen.width,
                height: window.screen.height
              }
            }
          }
        }

        const isPDP = document.querySelector('[data-cnstrc-product-detail][data-cnstrc-item-id]')
        const isPDPandHasSizes = isPDP && window.initialState?.adobeRecord?.otherSizes
        const isPDPandHasItemNumber = isPDP && isPDP?.dataset?.cnstrcItemId

        if (event.event_type === '[Amplitude] Page Viewed') {

          // hotfix for Amplitude bug with excludeReferrers logic
          if (event.event_properties.referring_domain === window.location.host) {
            delete event.event_properties.referring_domain
            delete event.event_properties.referrer
          }

          if (isPDPandHasSizes) {
            event.event_properties.pdpVariantItems = isPDPandHasSizes.map(size => size.productPartNumber)
          }

          if (isPDPandHasItemNumber) {
            event.event_properties.pdpItemNumber = isPDPandHasItemNumber
          }

          if (window?.sourceCodeObject?.changed === true) {
            event.user_properties = {
              ...event.user_properties,
              SourceCode: window.sourceCodeObject.sourceCode
            }
          }

          if (document.referrer) {
            event.event_properties.PreviousPagePath = (new URL(document.referrer)).pathname
          }

        }

        return event
      } catch (error) {
        const { sendToDatadog } = useDatadog()
        sendToDatadog(`Amplitude tracking enrichment error occurred: ${error?.message}`,
          {
            errorData: error,
            storageSessionId: Cookies.get('AMP_session_id'),
            event: event,
            url: window.location.href
          }
        )
        console.error('Amplitude page view tracking enrichment error: ', error)
        return event
      }
    }
  }
}

export const urlFilterEnrichment = () => {
  let lastPathname = null

  return {
    name: 'url-filter-enrichment',
    type: 'enrichment',
    execute: async (event) => {
      if (event.event_type === '[Amplitude] Page Viewed') {
        const currentUrl = new URL(window.location.href)
        const currentPathname = currentUrl.pathname

        if (currentPathname === lastPathname) {
          return null
        }

        lastPathname = currentPathname
      }
      return event
    }
  }
}

let amplitudeInitialized = false
let amplitudeInitPromise= null


export const initAmplitude = async () => {
  const { trackEvent: trackAmplitudeEvent } = useAmplitude()
  
  if (amplitudeInitialized) {
    return
  }

  if (!amplitudeInitPromise) {
    amplitudeInitPromise = (async () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.amplitude === 'undefined' &&
        typeof document !== 'undefined'
      ) {
        const amplitude = await import('@amplitude/analytics-browser')
        const instance = amplitude.createInstance()
        instance.add(urlFilterEnrichment())
        instance.add(pageViewTrackingEnrichment())
        instance.init(window.amplitudeApiKey, undefined, {
          transport: 'beacon',
          defaultTracking: {
            pageViews: true,
            sessions: false,
            fileDownloads: false,
            formInteractions: false,
            attribution: {
              excludeReferrers: [
                'hosted-pages.id.me',
                /paypal/i,
                /amazon/i,
                /cardinalcommerce/i,
                window.location.host
              ],
            }
          },
        })

        window.amplitude = instance
        amplitudeInitialized = true
        
        window.addEventListener("klaviyoForms", function (e) {
          const { type, metaData, ...rest } = e.detail || {}
          
          if (!type) {
            return
          }

          if (type === 'submit'){
            const { source, $step_name } = metaData || {}
            trackAmplitudeEvent(`klaviyo_${type}`, {
              PreviousPagePath: window.location.pathname,
              type,
              ...rest,
              source,
              stepName : $step_name,
            })
          }
        })
      }
    })()
  }

  await amplitudeInitPromise
}

initAmplitude()

if (
  typeof window !== 'undefined' &&
  typeof window.amplitudeAssetsHandler === 'undefined' &&
  typeof document !== 'undefined'
) {
  const mapAreas = {
    header: 'Header',
    footer: 'Footer',
    'left-navigation': 'Left Navigation',
    'top-navigation': 'Top Navigation',
    body: 'Body'
  }

  function getPagePositionForClick(target) {
    const parentArea = target.closest(
      '[data-site-area="header"], [data-site-area="footer"], [data-site-area="left-navigation"], [data-site-area="top-navigation"], [data-site-area="body"]'
    )
    return parentArea ? parentArea.getAttribute("data-site-area") : "undefined"
  }

  function sendAmplitudeAssetClickedEvent(target) {
    let assetName = undefined
    let altText = undefined
    let sectionType = target.closest('[data-homepage-section]')?.getAttribute('data-homepage-section') || undefined
    let sectionName = target.closest('[data-homepage-section-name]')?.getAttribute('data-homepage-section-name') || undefined
    let linkPosition = target.closest('[data-homepage-link-position]')?.getAttribute('data-homepage-link-position') || undefined
    const clickableElement = target.closest('button, a, [role="button"]')

    if (target.tagName === 'IMG') {
      assetName = target.getAttribute('src')?.split('/').pop() || 'Unnamed Image'
      altText = target.getAttribute('alt') || ''
    } else if (target.tagName === 'svg') {
      altText = clickableElement.getAttribute('aria-label') || clickableElement.getAttribute('data-tracking') || 'Unnamed SVG'
      assetName = 'SVG Element'
    } else if (target.closest('button, a, [role="button"]')) {
      assetName = clickableElement.textContent.trim() || target.getAttribute('title') || clickableElement.getAttribute('aria-label') || 'Unnamed Button'
    } else {
      assetName = target.textContent.trim() || 'Unnamed Asset'
    }
  
    const pagePosition = getPagePositionForClick(target)
  
    window.amplitude.track('asset clicked', {
      AssetName: assetName,
      Alt: altText,
      PageName: window.location.pathname,
      PagePosition: mapAreas[pagePosition] || undefined,
      SectionType: sectionType,
      SectionName: sectionName,
      LinkPosition: linkPosition
    })
  }
  
  document.addEventListener('DOMContentLoaded', () => {
    document.body.addEventListener('click', (event) => {
      try {
        const eventTarget = event.target
        const clickableElement = eventTarget.closest('a, button, [role="button"], img, svg, .clickable')
        if (clickableElement && event.target.closest('[data-site-area]')?.getAttribute('data-site-area') === 'body') sendAmplitudeAssetClickedEvent(clickableElement)
      } catch (error) {
        console.error('Error tracking asset clicks:', error)
      }
    })
  })

  window.amplitudeAssetsHandler = true
}
